import styled from 'styled-components';
import { variant, PositionProps } from 'styled-system';
import { TypographyProps, LayoutProps } from '../../../theme/styledProps';

export type ToggleSize = 'small' | 'large';

export const Switch = styled.span<{ variant: ToggleSize }>`
  display: inline-block;
  background: ${({ theme }) => theme.colors.neutral[500]};
  border-radius: 16px;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${variant<LayoutProps, ToggleSize>({
    variants: {
      small: {
        width: '32px',
        height: '16px',
      },
      large: {
        width: '48px',
        height: '24px',
      },
    },
  })}

  &::after {
    content: '';
    position: absolute;
    border-radius: 100%;
    top: 50%;
    left: 25%;
    background: ${({ theme }) => theme.colors.white};
    transform: translate(-50%, -50%);
    transition: all 0.2s ease;
    z-index: 1;

    ${variant<LayoutProps, ToggleSize>({
      variants: {
        small: {
          width: '12px',
          height: '12px',
        },
        large: {
          width: '20px',
          height: '20px',
        },
      },
    })}
  }
`;

export const Icons = styled.div<{ variant: ToggleSize }>`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  box-sizing: border-box;

  &:before {
    position: absolute;
    left: 8px;
    opacity: 0;
    font-family: 'Font Awesome 5 Pro';
    font-weight: 900;
    content: '\f00c';
    transition: opacity 0.3s ease;

    ${variant<PositionProps & TypographyProps, ToggleSize>({
      variants: {
        small: {
          left: '5px',
          fontSize: 'extraSmall',
        },
        large: {
          left: '8px',
          fontSize: 'small',
        },
      },
    })}
  }

  &:after {
    position: absolute;
    opacity: 1;
    font-family: 'Font Awesome 5 Pro';
    font-weight: 900;
    content: '\f00d';
    transition: opacity 0.3s ease;

    ${variant<PositionProps & TypographyProps, ToggleSize>({
      variants: {
        small: {
          right: '5px',
          fontSize: 'extraSmall',
        },
        large: {
          right: '8px',
          fontSize: 'small',
        },
      },
    })}
  }
`;

interface StyledToggleProps {
  disabled?: boolean;
}

export const StyledToggle = styled.label<StyledToggleProps>`
  cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
  display: inline-flex;
  align-items: center;
  position: relative;
  vertical-align: bottom;

  input {
    position: absolute;
    opacity: 0;
    z-index: -1;

    &:disabled {
      & ~ ${Switch} {
        background: ${({ theme }) => theme.colors.neutral[200]};
        &:after {
          background: ${({ theme }) => theme.colors.neutral[400]};
        }

        & ${Icons} {
          color: ${({ theme }) => theme.colors.neutral[400]};
        }
      }
    }

    &:checked:not(:disabled) {
      & ~ ${Switch} {
        background: ${({ theme }) => theme.colors.primary[500]};
      }
    }

    &:focus-visible {
      & ~ ${Switch} {
        box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary[300]};
      }
    }

    &:checked {
      & ~ ${Switch}:after {
        left: 75%;
      }
      & ~ ${Switch} {
        & ${Icons} {
          &:before {
            opacity: 1;
          }
          &:after {
            opacity: 0;
          }
        }
      }
    }
  }
`;

export const SettingToggle = styled(StyledToggle)({
  flexDirection: 'row-reverse',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});
